import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { getAreaTypeColor } from '../../SiteTakeoff/siteTakeoffConstants';

/**
 * Syncs an array of areas to Leaflet layers on the map.
 * Adds/updates/removes layers to match the current `areas` state.
 * Calls onAreaClick(areaId) when the user clicks a layer.
 */
export default function useAreaRendering({ mapRef, drawnItemsRef, areas, onAreaClick }) {
  const layerMapRef = useRef({}); // areaId -> leaflet layer

  useEffect(() => {
    const map = mapRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;

    const currentIds = new Set(areas.map(a => a._id || a.id));

    // Remove layers that no longer exist
    Object.keys(layerMapRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        const layer = layerMapRef.current[id];
        try { drawnItems.removeLayer(layer); } catch (_) {}
        delete layerMapRef.current[id];
      }
    });

    // Add/update layers for current areas
    areas.forEach(area => {
      const areaId = area._id || area.id;
      if (!area.paths?.length) return;

      // Already rendered — skip (updates happen via direct layer manipulation elsewhere)
      if (layerMapRef.current[areaId]) return;

      const color = getAreaTypeColor(area.areaType);
      let layer;

      if (area.geometryType === 'point') {
        const p = area.paths[0];
        if (!p?.lat || !p?.lng) return;
        layer = L.marker([p.lat, p.lng], {
          draggable: true,   // Disabled at the handler level by default — enabled via useAreaEditing
          autoPan: true,
          icon: L.divIcon({
            className: '',
            html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
          }),
        });
        // Disable drag initially — only useAreaEditing should enable it
        if (layer.dragging) {
          try { layer.dragging.disable(); } catch (_) {}
        }
      } else if (area.geometryType === 'ellipse') {
        const p = area.paths[0];
        if (!p?.lat || !p?.lng) return;
        const radiusMeters = (area.radiusX || 0) / 3.28084;
        layer = L.circle([p.lat, p.lng], {
          radius: radiusMeters,
          fillColor: color,
          fillOpacity: 0.25,
          color,
          weight: 2,
        });
      } else if (area.geometryType === 'polyline') {
        const latlngs = area.paths.map(p => [p.lat, p.lng]);
        layer = L.polyline(latlngs, { color, weight: 4, opacity: 0.8 });
      } else {
        // polygon (default)
        const latlngs = area.paths.map(p => [p.lat, p.lng]);
        layer = L.polygon(latlngs, {
          fillColor: color,
          fillOpacity: 0.3,
          color: area.strokeColor || '#000',
          weight: 2,
        });
      }

      if (!layer) return;

      layer.on('click', () => {
        if (onAreaClick) onAreaClick(areaId);
      });

      drawnItems.addLayer(layer);
      layerMapRef.current[areaId] = layer;
    });
  }, [areas, mapRef, drawnItemsRef, onAreaClick]);

  return { layerMapRef };
}
